import { CheckIcon, CloseIcon } from "./icons";
import type { ButtonHTMLAttributes, HTMLAttributes, ReactNode } from "react";
import { cn } from "@/shared/lib/cn";

export type StatusTone =
  "live" | "queued" | "warning" | "success" | "danger" | "archived";

export interface StatusLabelProps extends HTMLAttributes<HTMLSpanElement> {
  tone: StatusTone;
  detail?: ReactNode;
}

/** Explicit status text stays readable on saturated, solid fills. */
export function StatusLabel({
  tone,
  detail,
  children,
  className,
  ...props
}: StatusLabelProps) {
  return (
    <span
      className={cn(
        "inline-flex min-h-[22px] w-fit max-w-full items-center gap-1.5 rounded-pill px-[9px] py-1 text-[11px] font-semibold",
        statusTone[tone],
        className,
      )}
      {...props}
    >
      <span>{children}</span>
      {detail ? (
        <span className="font-normal tabular-nums opacity-75">{detail}</span>
      ) : null}
    </span>
  );
}

const statusTone: Record<StatusTone, string> = {
  live: "bg-action-bg text-action-text",
  queued: "bg-canvas-soft text-ink-soft",
  warning: "bg-warning text-[var(--warning-on)]",
  success: "bg-[var(--status-success)] text-white",
  danger: "bg-danger-solid text-white",
  archived: "bg-[var(--status-archived)] text-white",
};

export interface ChipProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  selected?: boolean;
  removable?: boolean;
}

export function Chip({
  selected,
  removable,
  className,
  children,
  ...props
}: ChipProps) {
  return (
    <button
      type="button"
      aria-pressed={removable ? undefined : selected}
      data-selected={selected || undefined}
      data-removable={removable || undefined}
      className={cn(
        "studio-chip inline-flex items-center gap-1.5 rounded-sm px-3 text-[12.5px] font-medium outline-none focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--focus-ring)] disabled:cursor-not-allowed disabled:opacity-45",
        className,
      )}
      {...props}
    >
      {selected && !removable ? <CheckIcon className="size-3.5 shrink-0" /> : null}
      {children}
      {removable ? (
        <CloseIcon className="size-3.5 shrink-0" />
      ) : null}
    </button>
  );
}

export interface ProgressBarProps {
  value: number;
  label: string;
  max?: number;
  showLabel?: boolean;
  "aria-valuetext"?: string;
  tone?:
    | "live"
    | "warning"
    | "neutral"
    | "routine"
    | "danger"
    | "image"
    | "video"
    | "voice"
    | "text"
    | "asset";
  className?: string;
}

export function ProgressBar({
  value,
  label,
  tone = "routine",
  max = 100,
  showLabel = true,
  "aria-valuetext": valueText,
  className,
}: ProgressBarProps) {
  const upper = Number.isFinite(max) && max > 0 ? max : 100;
  const clamped = Math.min(upper, Math.max(0, Number.isFinite(value) ? value : 0));
  const percent = clamped / upper * 100;
  return (
    <div className={cn("flex min-w-0 flex-col gap-[7px]", className)}>
      {showLabel ? <div className="flex min-w-0 items-baseline justify-between gap-3 text-[12.5px] text-ink-soft">
        <span className="min-w-0 truncate">{label}</span>
        <span
          className={cn(
            "shrink-0 font-medium tabular-nums text-ink",
            tone === "danger" && "text-danger",
          )}
        >
          {Math.round(percent)}%
        </span>
      </div> : null}
      <div
        role="progressbar"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={upper}
        aria-valuenow={clamped}
        aria-valuetext={valueText}
        className="h-2 overflow-hidden rounded-pill bg-[var(--progress-track)]"
      >
        <div
          className={cn("h-full rounded-pill", progressTone[tone])}
          style={{ width: `${percent}%` }}
        />
      </div>
    </div>
  );
}

export interface SkeletonProps extends HTMLAttributes<HTMLDivElement> {
  label?: string;
}

export function Skeleton({
  label = "Loading",
  className,
  ...props
}: SkeletonProps) {
  return (
    <div
      role="status"
      aria-label={label}
      className={cn("rounded-[4px] bg-canvas-soft", className)}
      {...props}
    >
      <span className="sr-only">{label}</span>
    </div>
  );
}

const progressTone: Record<NonNullable<ProgressBarProps["tone"]>, string> = {
  live: "bg-accent",
  routine: "bg-media-video",
  neutral: "bg-ink",
  warning: "bg-warning",
  danger: "bg-danger",
  image: "bg-media-image",
  video: "bg-media-video",
  voice: "bg-media-voice",
  text: "bg-media-text",
  asset: "bg-media-asset",
};

export type MediaTone =
  | "neutral"
  | "image"
  | "video"
  | "voice"
  | "text"
  | "asset";

export interface MediaTileProps
  extends Omit<HTMLAttributes<HTMLElement>, "title"> {
  title: ReactNode;
  description?: ReactNode;
  media?: ReactNode;
  meta?: ReactNode;
  status?: ReactNode;
  actions?: ReactNode;
  tone?: MediaTone;
}

/** Presentational media record. Wrap in a link or add actions explicitly when interactive. */
export function MediaTile({
  title,
  description,
  media,
  meta,
  status,
  actions,
  tone = "neutral",
  className,
  ...props
}: MediaTileProps) {
  return (
    <article
      className={cn("min-w-0 overflow-hidden rounded-lg bg-paper", className)}
      {...props}
    >
      <div
        className={cn(
          "grid aspect-[4/3] min-h-28 place-items-center overflow-hidden",
          mediaTileTone[tone],
          "[&_img]:size-full [&_img]:object-cover [&_video]:size-full [&_video]:object-cover",
        )}
      >
        {media}
      </div>
      <div className="flex min-w-0 items-start gap-3 p-3.5">
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 items-center gap-2">
            <h3 className="min-w-0 flex-1 truncate text-[13.5px] font-semibold leading-[1.4] text-ink">
              {title}
            </h3>
            {status ? <div className="shrink-0">{status}</div> : null}
          </div>
          {description ? (
            <p className="mt-1 line-clamp-2 text-xs leading-[1.55] text-ink-soft">
              {description}
            </p>
          ) : null}
          {meta ? (
            <div className="mt-2 text-[11px] leading-[1.45] text-ink-muted">
              {meta}
            </div>
          ) : null}
        </div>
        {actions ? <div className="shrink-0">{actions}</div> : null}
      </div>
    </article>
  );
}

export interface StateItemProps
  extends Omit<HTMLAttributes<HTMLDivElement>, "title"> {
  title: ReactNode;
  description?: ReactNode;
  icon?: ReactNode;
  status?: ReactNode;
  action?: ReactNode;
  tone?: MediaTone | "success" | "warning" | "danger";
}

/** Compact, reusable row for generation, upload, and delivery states. */
export function StateItem({
  title,
  description,
  icon,
  status,
  action,
  tone = "neutral",
  className,
  ...props
}: StateItemProps) {
  return (
    <div
      className={cn(
        "flex min-w-0 items-center gap-3 rounded-md bg-paper p-3",
        className,
      )}
      {...props}
    >
      {icon ? (
        <span
          aria-hidden="true"
          className={cn(
            "grid size-9 shrink-0 place-items-center rounded-sm [&_svg]:size-4",
            stateItemTone[tone],
          )}
        >
          {icon}
        </span>
      ) : null}
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-center gap-2">
          <span className="min-w-0 flex-1 truncate text-[13px] font-semibold text-ink">
            {title}
          </span>
          {status ? <span className="shrink-0">{status}</span> : null}
        </div>
        {description ? (
          <p className="mt-0.5 truncate text-xs text-ink-soft">{description}</p>
        ) : null}
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  );
}

const mediaTileTone: Record<MediaTone, string> = {
  neutral: "bg-canvas-soft text-ink-muted",
  image: "bg-media-image-subtle text-media-image-text",
  video: "bg-media-video-subtle text-media-video-text",
  voice: "bg-media-voice-subtle text-media-voice-text",
  text: "bg-media-text-subtle text-media-text-text",
  asset: "bg-media-asset-subtle text-media-asset-text",
};

const stateItemTone: Record<
  NonNullable<StateItemProps["tone"]>,
  string
> = {
  ...mediaTileTone,
  success: "bg-[var(--status-success)] text-white",
  warning: "bg-warning text-[var(--warning-on)]",
  danger: "bg-danger-solid text-white",
};

export function Separator({
  orientation = "horizontal",
  label,
  className,
}: {
  orientation?: "horizontal" | "vertical";
  label?: string;
  className?: string;
}) {
  if (label) {
    return (
      <div
        role="separator"
        className={cn("flex items-center gap-3", className)}
      >
        <span className="h-px flex-1 bg-line-soft" />
        <span className="text-[11px] font-medium uppercase tracking-[0.09em] text-ink-muted">
          {label}
        </span>
        <span className="h-px flex-1 bg-line-soft" />
      </div>
    );
  }
  return (
    <span
      role="separator"
      aria-orientation={orientation}
      className={cn(
        orientation === "horizontal"
          ? "block h-px w-full bg-line-soft"
          : "block h-3.5 w-px bg-line",
        className,
      )}
    />
  );
}
